import * as React from 'react';
import ReactDOM from 'react-dom';

export type Props = {
    host?: HTMLElement | null;
    children?: React.ReactNode;
    className?: string;
    onMount?: () => void;
    onUnmount?: () => void;
    appendParent?: boolean;
};

// use this context to retrieve latest in portal in the context of the current component
// closable component uses it
export const PortalContext = React.createContext<HTMLElement | null>(null);

const Portal = ({ appendParent, host, children, className, onMount, onUnmount }: Props): JSX.Element => {
    const parentPortalNode = React.useContext(PortalContext);

    const defaultHostNode = host || document.body;
    const hostNode = appendParent ? parentPortalNode ?? defaultHostNode : defaultHostNode;

    const [container] = React.useState(() => {
        const element = document.createElement('div');
        hostNode.appendChild(element); // append before initial render to let initial animations to go through fine
        return element;
    });

    React.useEffect(() => {
        if (className) {
            container.classList.add(...className.split(' ').filter(Boolean));
        }
        if (container.parentElement !== hostNode) {
            hostNode.appendChild(container);
        }

        onMount?.();

        return () => {
            hostNode.removeChild(container);
            onUnmount?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [host]);

    React.useEffect(() => {
        container.setAttribute('class', className ?? '');
    }, [container, className]);

    return <PortalContext.Provider value={container}>{ReactDOM.createPortal(children, container)}</PortalContext.Provider>;
};

export default Portal;
